/**
 * Browser Web Push helpers for meeting reminders (VAPID).
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

export function pushSupported() {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export async function ensurePushServiceWorker() {
  if (!pushSupported()) return null;
  const reg = await navigator.serviceWorker.register('/sw.js');
  await navigator.serviceWorker.ready;
  return reg;
}

/**
 * @param {(path: string, opts?: object) => Promise<any>} apiFetch
 */
export async function enableMeetingReminderPush(apiFetch) {
  if (!pushSupported()) {
    return { ok: false, reason: 'unsupported' };
  }
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    return { ok: false, reason: 'denied' };
  }

  // A server without VAPID keys answers 503, which apiFetch turns into a generic
  // "temporarily unavailable" throw. Swallow it so the caller can report the real
  // cause instead of telling the user to retry something that will never succeed.
  let keyRes = null;
  try {
    keyRes = await apiFetch('/push/vapid-public-key');
  } catch (_) {
    return { ok: false, reason: 'not_configured' };
  }
  if (!keyRes?.publicKey) {
    return { ok: false, reason: 'not_configured' };
  }

  const reg = await ensurePushServiceWorker();
  if (!reg) return { ok: false, reason: 'sw_failed' };

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(keyRes.publicKey),
    });
  }

  await apiFetch('/push/subscribe', {
    method: 'POST',
    body: { subscription: sub.toJSON() },
  });

  return { ok: true };
}

/** True when this browser already has an active MeetAI push subscription. */
export async function hasLocalPushSubscription() {
  if (!pushSupported()) return false;
  try {
    const reg = await navigator.serviceWorker.getRegistration('/sw.js');
    if (!reg) return false;
    const sub = await reg.pushManager.getSubscription();
    return !!sub;
  } catch (_) {
    return false;
  }
}

/**
 * @param {(path: string, opts?: object) => Promise<any>} apiFetch
 */
export async function disableMeetingReminderPush(apiFetch) {
  if (!pushSupported()) return { ok: true };
  try {
    const reg = await navigator.serviceWorker.getRegistration('/sw.js');
    const sub = reg ? await reg.pushManager.getSubscription() : null;
    if (sub) {
      const endpoint = sub.endpoint;
      try {
        await sub.unsubscribe();
      } catch (_) {}
      try {
        await apiFetch('/push/subscribe', {
          method: 'DELETE',
          body: { endpoint },
        });
      } catch (_) {}
    } else {
      try {
        await apiFetch('/push/subscribe', { method: 'DELETE', body: {} });
      } catch (_) {}
    }
  } catch (_) {}
  return { ok: true };
}

/**
 * For users who only log in (Settings default ON / 10 min):
 * sync prefs to server, then always request notification permission and subscribe.
 * @param {(path: string, opts?: object) => Promise<any>} apiFetch
 */
export async function ensureDefaultReminderPushOnLogin(apiFetch, prefs = {}) {
  if (!pushSupported()) return { ok: false, reason: 'unsupported' };
  if (prefs.remindersEnabled === false) return { ok: false, reason: 'disabled' };

  const reminderMinutes = Number(prefs.reminderMinutes) || 10;
  const timezone = prefs.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;

  try {
    await apiFetch('/push/prefs', {
      method: 'PUT',
      body: { remindersEnabled: true, reminderMinutes, timezone },
    });
  } catch (err) {
    return { ok: false, reason: 'prefs_failed', error: err?.message };
  }

  const permission = typeof Notification !== 'undefined' ? Notification.permission : 'denied';
  if (permission === 'denied') {
    return { ok: false, reason: 'denied' };
  }

  // Always prompt / re-subscribe after login when not blocked.
  return enableMeetingReminderPush(apiFetch);
}

/** Load reminder prefs from DB (source of truth for soft prompt / Settings). */
export async function fetchMeetingReminderPrefs(apiFetch) {
  try {
    const data = await apiFetch('/push/prefs');
    if (!data || typeof data !== 'object') return null;
    return {
      remindersEnabled: data.remindersEnabled !== false,
      reminderMinutes: Number(data.reminderMinutes) || 10,
      timezone: data.timezone || null,
      softPromptDismissedAt: data.softPromptDismissedAt || null,
      remindersSetupAt: data.remindersSetupAt || null,
      hasSubscription: data.hasSubscription === true,
      pushConfigured: data.pushConfigured === true,
    };
  } catch (_) {
    return null;
  }
}

/** Persist "Maybe later" to DB so soft prompt stays dismissed across devices/sessions. */
export async function dismissMeetingReminderSoftPrompt(apiFetch, prefs = {}) {
  return apiFetch('/push/prefs', {
    method: 'PUT',
    body: {
      remindersEnabled: prefs.remindersEnabled !== false,
      reminderMinutes: Number(prefs.reminderMinutes) || 10,
      timezone: prefs.timezone,
      softPromptDismissed: true,
    },
  });
}

/** Mark account as having completed reminders enable (DB). */
export async function markMeetingRemindersSetup(apiFetch, prefs = {}) {
  return apiFetch('/push/prefs', {
    method: 'PUT',
    body: {
      remindersEnabled: true,
      reminderMinutes: Number(prefs.reminderMinutes) || 10,
      timezone: prefs.timezone,
      remindersSetup: true,
    },
  });
}

/**
 * Sync Settings toggle → server prefs + browser push subscription.
 * @param {(path: string, opts?: object) => Promise<any>} apiFetch
 */
export async function syncMeetingReminderPrefs(apiFetch, prefs = {}) {
  const remindersEnabled = prefs.remindersEnabled !== false;
  const reminderMinutes = Number(prefs.reminderMinutes) || 10;
  const timezone = prefs.timezone || undefined;

  await apiFetch('/push/prefs', {
    method: 'PUT',
    body: {
      remindersEnabled,
      reminderMinutes,
      timezone,
      ...(remindersEnabled ? {} : {}),
    },
  });

  if (remindersEnabled) {
    return enableMeetingReminderPush(apiFetch);
  }
  return disableMeetingReminderPush(apiFetch);
}

/**
 * Auto-sync on window focus / visibility change:
 * If the user previously had permission 'default' or was unblocking in browser settings,
 * re-check permission and register push immediately if granted.
 * @param {(path: string, opts?: object) => Promise<any>} apiFetch
 */
export function setupPushPermissionFocusListener(apiFetch, onPermissionChange) {
  if (typeof window === 'undefined' || typeof Notification === 'undefined' || !pushSupported()) {
    return () => {};
  }

  let lastPerm = Notification.permission;

  const check = async () => {
    const currentPerm = Notification.permission;
    if (currentPerm !== lastPerm) {
      lastPerm = currentPerm;
      if (typeof onPermissionChange === 'function') {
        onPermissionChange(currentPerm);
      }
      if (currentPerm === 'granted') {
        try {
          await enableMeetingReminderPush(apiFetch);
        } catch (_) {}
      }
    }
  };

  const handleVisibility = () => {
    if (document.visibilityState === 'visible') {
      check();
    }
  };

  window.addEventListener('focus', check);
  document.addEventListener('visibilitychange', handleVisibility);

  // Also listen on navigator.permissions if available
  let permStatusObj = null;
  if (navigator?.permissions?.query) {
    navigator.permissions.query({ name: 'notifications' }).then((status) => {
      permStatusObj = status;
      permStatusObj.onchange = () => check();
    }).catch(() => {});
  }

  return () => {
    window.removeEventListener('focus', check);
    document.removeEventListener('visibilitychange', handleVisibility);
    if (permStatusObj) {
      permStatusObj.onchange = null;
    }
  };
}

